import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnInit,
  Output,
  inject,
  DestroyRef,
  input,
  signal,
  linkedSignal,
  viewChild,
} from '@angular/core';
import {
  TaskableTask,
  UploadTaskDocumentGQL,
  DeleteTaskDocumentGQL,
  UploadTaskDocumentMutation,
  UpdateTasksDatesGQL,
  UpdateTasksDatesMutation,
} from '@app/generated/graphql';
import {
  UntypedFormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Apollo } from 'apollo-angular';
import { ConfirmModalComponent } from '@app/core/components/confirm-modal/confirm-modal.component';
import { ToastService } from '@app/core/services/toast/toast.service';
import {
  DropzoneComponent,
  DropzoneConfigInterface,
  DropzoneModule,
} from 'ngx-dropzone-wrapper';
import { ModalService } from '@app/core/services/modal/modal.service';
import { FieldType } from '@app/core/interfaces/table.interface';
import { Ag1TableService } from '../ag1-table/ag1-table.service';
import * as Sentry from '@sentry/browser';
import {
  IExtendedTask,
  IOutputFile,
} from '@app/account/shipments/documents-tab/documents-tab.component';
import { Ag1ButtonSmallComponent } from '../ag1-button-small/ag1-button-small.component';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { Ag1ButtonComponent } from '../ag1-button/ag1-button.component';
import { SpinnerComponent } from '../../spinner/spinner.component';
import { Ag1TableBaseOldComponent } from '../ag1-table/components/ag1-table-base/ag1-table-base-old.component';
import { Ag1LinkComponent } from '../ag1-link/ag1-link.component';
import { TruncatePipe } from '../../pipes/truncate.pipe';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'ag1-upload-modal',
  templateUrl: './ag1-upload-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [Ag1TableService],
  imports: [
    Ag1ButtonSmallComponent,
    AngularSvgIconModule,
    FormsModule,
    ReactiveFormsModule,
    DropzoneModule,
    Ag1ButtonComponent,
    SpinnerComponent,
    Ag1TableBaseOldComponent,
    Ag1LinkComponent,
    TruncatePipe,
  ],
})
export class Ag1UploadModalComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private ag1TableService = inject(Ag1TableService);
  private apollo = inject(Apollo);
  private toastService = inject(ToastService);
  private formBuilder = inject(UntypedFormBuilder);
  private uploadTaskDocument = inject(UploadTaskDocumentGQL);
  private deleteTaskDocument = inject(DeleteTaskDocumentGQL);
  private modalService = inject(ModalService);
  private cdr = inject(ChangeDetectorRef);
  private updateTasksDates = inject(UpdateTasksDatesGQL);

  //Subscription cleanup
  readonly taskTitle = input.required<string>();
  /** Parent seeds the task one-way; local copy is updated after upload/date changes. */
  readonly task = input.required<IExtendedTask>();
  readonly localTask = linkedSignal(() => ({ ...this.task() }));
  readonly align = input<'left' | 'right'>(undefined);
  /** Parent may seed position; local copies are clamped for the viewport. */
  readonly top = input<number>(undefined);
  readonly left = input<number>(undefined);
  readonly localTop = signal(0);
  readonly localLeft = signal(0);
  readonly right = input<number>(undefined);

  public config: DropzoneConfigInterface = {
    clickable: true,
    maxFiles: 1,
    autoReset: null,
    errorReset: null,
    cancelReset: null,
    url: '/fake/path',
    acceptedFiles: '*',
    createImageThumbnails: false,
    previewsContainer: false,
    autoProcessQueue: false,
  };

  readonly componentRef = viewChild(DropzoneComponent);

  @Output()
  uploadDestroyEvent: EventEmitter<TaskableTask> =
    new EventEmitter<TaskableTask>();

  // detailed view
  showUploadPanel: boolean = false;
  isUploadPanelLoading: boolean = true;

  // viewspace config
  vh = Math.max(
    document.documentElement.clientHeight || 0,
    window.innerHeight || 0,
  );
  vw = Math.max(
    document.documentElement.clientWidth || 0,
    window.innerWidth || 0,
  );
  maxWidth: number = 400;
  uploadDate: any;
  maxTaskDate: string = '';

  // docs
  file: File;
  fileUploading: boolean = false;
  disableUpload: boolean = true;
  fields: FieldType[] = [
    {
      name: 'filename',
      displayName: 'Auto-Generated Files',
      checked: true,
    },
  ];

  uploadForm = this.formBuilder.group({
    document: [null, [Validators.required]],
    uploadDate: [{ value: null, disabled: true }, []],
    customDate: [null, []],
  });

  constructor() {}

  ngOnInit(): void {
    this.localTop.set(this.top() ?? 0);
    this.localLeft.set(this.left() ?? 0);

    if (this.right() + this.maxWidth > this.vw - 10) {
      this.maxWidth = this.vw - this.right() - 10;
    }
    if (this.align() == 'left') {
      this.localLeft.update((left) => left - this.maxWidth);
    }
    if (this.localTop() + 490 > this.vh) {
      this.localTop.set(this.vh - 490);
    }
    if (this.localTask().uploadedAt) {
      this.uploadForm
        .get('uploadDate')
        .setValue(this.formatDate(new Date(this.localTask().uploadedAt)));
    } else {
      this.uploadForm.get('uploadDate').setValue(this.formatDate(new Date()));
    }
    if (this.localTask().customDate) {
      this.uploadForm
        .get('customDate')
        .setValue(this.formatDate(new Date(this.localTask().customDate)));
    }

    this.uploadForm
      .get('customDate')
      ?.valueChanges.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((newValue) => {
        if (this.localTask().file && !this.file) {
          if (newValue === '') {
            this.uploadForm.get('customDate').setValue(null);
          }
          this.disableUpload = false;
        }
      });

    this.ag1TableService.updateIsLoading(false);
    this.ag1TableService.updateFields(this.fields);
    this.ag1TableService.updateData(this.localTask().platformDocuments);
  }

  closeComponent(): void {
    this.uploadDestroyEvent.emit(this.localTask());
  }

  formatDate(date: Date): string {
    return [
      date.getFullYear(),
      this.padTo2Digits(date.getMonth() + 1),
      this.padTo2Digits(date.getDate()),
    ].join('-');
  }

  padTo2Digits(num: number): string {
    return num.toString().padStart(2, '0');
  }

  uploadFile(): void {
    this.fileUploading = true;
    this.apollo
      .mutate<UploadTaskDocumentMutation>({
        mutation: this.uploadTaskDocument.document,
        variables: {
          uploadDate: (this.uploadDate
            ? this.uploadDate
            : new Date()
          ).toISOString(),
          customDate: this.uploadForm.value.customDate
            ? this.uploadForm.value.customDate
            : null,
          taskId: parseInt(this.localTask().id),
          file: this.file,
        },
      })
      .subscribe(
        (res) => {
          if (
            res.data?.uploadTaskDocument?.errors &&
            res.data?.uploadTaskDocument?.errors.length > 0
          ) {
            Sentry.captureException(res.data.uploadTaskDocument.errors);
            this.fileUploading = false;
            this.closeComponent();
            this.toastService.showErrorToast('Could not upload file');
            console.error(res.data.uploadTaskDocument.errors);
          } else {
            this.fileUploading = false;
            const response = res.data.uploadTaskDocument;
            this.localTask.update((task) => ({
              ...task,
              file: response.task.file,
              customDate: response.task.customDate,
              uploadedAt: response.task.uploadedAt,
            }));
            this.toastService.showSuccessToast('File uploaded successfully');
            this.closeComponent();
          }
        },
        (err) => {
          Sentry.captureException(err);
          this.toastService.showErrorToast('Could not upload file');
          console.error(err);
        },
      );
  }

  updateCustomDateOnly(): void {
    if (!this.localTask().customDate && !this.uploadForm.value.customDate) {
      console.error(
        'No file dropped and no custom Date filled in but upload button was clicked',
      );
      Sentry.captureException(
        'Uploader: No file dropped and no custom Date filled in but upload button was clicked',
      );
      this.toastService.showToast(
        'Please upload a file or update the custom date',
      );
      return;
    }
    this.fileUploading = true;
    this.apollo
      .mutate<UpdateTasksDatesMutation>({
        mutation: this.updateTasksDates.document,
        variables: {
          updateValues: JSON.parse(
            `{"${this.localTask().id}": "${this.uploadForm.value.customDate}"}`,
          ),
        },
      })
      .subscribe(
        (_) => {
          this.fileUploading = false;
          this.localTask.update((task) => ({
            ...task,
            customDate: this.uploadForm.value.customDate,
          }));
          this.toastService.showSuccessToast('Date updated successfully');
          this.closeComponent();
        },
        (err) => {
          console.error(err);
          Sentry.captureException(err);
          this.toastService.showErrorToast('Could not update custom date');
        },
      );
  }

  submitForm(): void {
    // The logic is that if there has been a file uploaded previously (this.localTask().file) and no file dropped in the uploader,
    // it must be just a date updated
    if (this.localTask().file && !this.file) {
      this.updateCustomDateOnly();
    } else {
      this.uploadFile();
    }
  }

  dropzoneEvent(event: File): void {
    if (this.file) {
      if (this.toastService.toastCounter) {
        return;
      }
      this.toastService.showErrorToast('Only a single file can be uploaded');
    } else {
      this.file = event;
      this.disableUpload = false;
    }
  }

  removeFromUpload(): void {
    this.componentRef().directiveRef.reset();
    this.file = null;
    this.disableUpload = true;
  }

  showDeleteDocumentConfirmModal(): void {
    const modalInstance = this.modalService.showConfirm({
      title: 'Delete document?',
      content: `Are you sure you want to delete <b>${this.localTask().file.filename}</b>? This action cannot be undone.`,
      confirmBtnText: 'Delete document',
      confirmBtnClass: 'is-danger',
      onConfirm: () =>
        this.deleteDocument(modalInstance, parseInt(this.localTask().id)),
    });
  }

  deleteDocument(modalInstance: ConfirmModalComponent, id: number): void {
    modalInstance.setLoading(true);
    // below is depreciated, we now use this mutation differently
    // this.deleteTaskDocument.mutate({ id }).subscribe(
    //   (res) => {
    //     this.localTask.update((task) => ({
    //       ...task,
    //       file: null,
    //       uploadedAt: null,
    //       customDate: null,
    //     }));
    //     this.uploadForm.get('customDate').setValue(null);
    //     this.uploadForm.get('uploadDate').setValue(null);
    //     this.cdr.detectChanges();
    //     modalInstance.hide();
    //     this.toastService.showToast('Document deleted');
    //   },
    //   (err) => {
    //     modalInstance.hide();
    //     this.toastService.showErrorToast('Failed to delete shipment document');
    //     throw err;
    //   }
    // );
  }

  onDownloadClick(item: IOutputFile): void {
    window.open(item.v2Url, '_blank');
  }
}
